import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { PROJECTS } from "@/data/site";
import { SectionTag } from "@/components/Kinetic";
import { VideoPreview } from "@/components/VideoPreview";
import { EASE, fadeUp, viewport } from "@/lib/motion";

/* True format per project — mixed ratios are intentional. */
const TREATMENT = {
  v916: { ratio: "aspect-[9/16]", span: "lg:col-span-4", vertical: true },
  r45: { ratio: "aspect-[4/5]", span: "lg:col-span-5", vertical: true },
  w169: { ratio: "aspect-[16/9]", span: "lg:col-span-8", vertical: false },
};

const ytFallback = (e) => {
  if (!e.currentTarget.dataset.fallback) {
    e.currentTarget.dataset.fallback = "1";
    e.currentTarget.src = e.currentTarget.src.replace(
      "maxresdefault",
      "hqdefault"
    );
  }
};

const TitleCard = ({ p }) => (
  <div className="absolute inset-0 flex flex-col items-center justify-center overflow-hidden">
    <div
      className="absolute inset-0"
      style={{
        background:
          "radial-gradient(120% 120% at 50% 0%, #1a1a1a 0%, #0a0a0a 65%)",
      }}
    />
    <div className="absolute left-0 top-[26%] h-[1px] w-full bg-[#d4ff00]/15" />
    <div className="relative flex flex-col items-center px-6 text-center">
      <img src="/brand/hsk-logo.png" alt="HSK" className="h-6 w-auto" />
      <span className="mt-5 font-display text-xl font-black uppercase leading-[0.95] tracking-[-0.03em] text-[#e5e5e5] md:text-2xl">
        {p.name}
      </span>
      <span className="mt-4 label text-[#a3a3a3]">{p.format}</span>
    </div>
  </div>
);

const Row = ({ p, onOpen, i }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);
  const t = TREATMENT[p.treatment];
  const inView = useInView(ref, { once: true, amount: 0.02 });
  const [hover, setHover] = useState(false);
  const flip = i % 2 === 1;

  const mediaInner = (
    <motion.div
      initial={{ clipPath: "inset(0 0 100% 0)" }}
      animate={inView ? { clipPath: "inset(0 0 0% 0)" } : {}}
      transition={{ duration: 1.1, ease: EASE }}
      className={`relative w-full overflow-hidden ${t.ratio}`}
    >
      {p.image ? (
        <motion.img
          src={p.image}
          alt={`${p.name} — project still`}
          loading="lazy"
          onError={ytFallback}
          style={{ y, objectPosition: p.objectPosition || "center" }}
          animate={{
            scale: hover ? 1.04 : 1,
            filter: hover ? "brightness(1)" : "brightness(0.88)",
          }}
          transition={{ duration: 0.7, ease: EASE }}
          className="absolute inset-0 h-[112%] w-full object-cover"
        />
      ) : (
        <TitleCard p={p} />
      )}

      <VideoPreview id={p.youtubeId} active={hover} vertical={t.vertical} />

      <div className="pointer-events-none absolute inset-0 bg-[#0a0a0a]/20" />
      <div className="scanlines pointer-events-none absolute inset-0 opacity-30" />

      <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-end p-4 md:p-5">
        <span className="label border border-[#e5e5e5]/25 bg-[#0a0a0a]/70 px-3 py-1.5 text-[#e5e5e5] backdrop-blur">
          {p.badge}
        </span>
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between p-4 md:p-6">
        <span className="label bg-[#0a0a0a]/75 px-3 py-1.5 text-[#d4ff00] backdrop-blur">
          {p.kicker}
        </span>
        <motion.span
          animate={{ opacity: hover ? 1 : 0, y: hover ? 0 : 8 }}
          transition={{ duration: 0.35, ease: EASE }}
          className="label bg-[#d4ff00] px-3 py-1.5 text-[#0a0a0a]"
        >
          {p.link ? "WATCH PROJECT ↗" : "OPEN"}
        </motion.span>
      </div>
    </motion.div>
  );

  const mediaClass = `${flip ? "lg:order-1" : ""} ${t.span} group relative block w-full overflow-hidden bg-[#111] outline-none focus-visible:ring-1 focus-visible:ring-[#d4ff00]`;

  return (
    <div
      ref={ref}
      data-testid={`project-row-${p.id}`}
      className="relative grid grid-cols-1 items-center gap-6 border-t border-[#1f1f1f] py-10 lg:grid-cols-12 lg:gap-12 lg:py-16"
    >
      <div className={`${flip ? "lg:order-2" : ""} lg:col-span-4`}>
        <div className="flex items-baseline gap-4">
          <span className="font-mono text-sm tracking-[0.2em] text-[#d4ff00]">
            {p.index}
          </span>
          <span className="h-[1px] flex-1 bg-[#1f1f1f]" />
        </div>
        <motion.h3
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="mt-5 font-display text-3xl font-black uppercase leading-[0.9] tracking-[-0.03em] text-[#e5e5e5] md:text-[3.2vw] lg:text-[2.3vw]"
        >
          {p.name}
        </motion.h3>
        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          custom={1}
          className="mt-4 max-w-sm text-sm leading-relaxed text-[#a3a3a3]"
        >
          {p.summary}
        </motion.p>

        <dl className="mt-7 grid grid-cols-2 gap-y-4 border-t border-[#1f1f1f] pt-6">
          {[
            ["CATEGORY", p.category],
            ["ROLE", p.role],
            ["TYPE", p.type],
            ["FORMAT", p.format],
            ["TOOLS", p.tools.join(" / ")],
            ["YEAR", p.year],
          ].map(([k, v]) => (
            <div key={k} className={k === "TYPE" ? "col-span-2" : ""}>
              <dt className="label text-[#7a7a7a]">{k}</dt>
              <dd className="mt-1 text-sm text-[#e5e5e5]">{v}</dd>
            </div>
          ))}
        </dl>

        <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
          <button
            type="button"
            onClick={() => onOpen(p)}
            data-testid={`project-open-${p.id}`}
            data-cursor="open"
            className="group flex items-center gap-3"
          >
            <span className="label text-[#e5e5e5] transition-colors group-hover:text-[#d4ff00]">
              OPEN PROJECT
            </span>
            <span className="h-[1px] w-8 bg-[#2a2a2a] transition-all duration-500 group-hover:w-14 group-hover:bg-[#d4ff00]" />
          </button>
          {p.link && (
            <a
              href={p.link}
              target="_blank"
              rel="noreferrer"
              data-cursor="play"
              data-testid={`project-watch-${p.id}`}
              className="group flex items-center gap-3"
            >
              <span className="label text-[#d4ff00]">WATCH PROJECT ↗</span>
              <span className="h-[1px] w-8 bg-[#d4ff00]/40 transition-all duration-500 group-hover:w-14 group-hover:bg-[#d4ff00]" />
            </a>
          )}
        </div>
      </div>

      {p.link ? (
        <a
          href={p.link}
          target="_blank"
          rel="noreferrer"
          aria-label={`Watch ${p.name} on YouTube`}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onFocus={() => setHover(true)}
          onBlur={() => setHover(false)}
          data-cursor="play"
          data-testid={`project-media-${p.id}`}
          className={mediaClass}
        >
          {mediaInner}
        </a>
      ) : (
        <button
          type="button"
          onClick={() => onOpen(p)}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          onFocus={() => setHover(true)}
          onBlur={() => setHover(false)}
          aria-label={`Open ${p.name}`}
          data-cursor="open"
          data-testid={`project-media-${p.id}`}
          className={mediaClass}
        >
          {mediaInner}
        </button>
      )}
    </div>
  );
};

export const SelectedWork = ({ onOpen }) => (
  <section
    id="work"
    data-testid="selected-work"
    className="relative border-t border-[#1f1f1f] py-20 md:py-32"
  >
    <div className="mx-auto max-w-[1700px] px-5 md:px-10">
      <SectionTag num="01 / WORK">SELECTED EDITS</SectionTag>

      <div className="mb-12 grid gap-8 lg:grid-cols-12">
        <h2 className="font-display text-4xl font-black uppercase leading-[0.88] tracking-[-0.04em] text-[#e5e5e5] lg:col-span-7 lg:text-[3.9vw]">
          DIFFERENT SOURCES.
          <br />
          ONE <span className="text-[#d4ff00]">STANDARD.</span>
        </h2>
        <p className="max-w-md self-end text-sm leading-relaxed text-[#a3a3a3] lg:col-span-5">
          Short-form and podcast cuts, motion-led social, product reveals, a
          spec commercial, event promos and stream highlights. Hover any frame
          to preview it — each piece keeps its own format.
        </p>
      </div>

      {PROJECTS.map((p, i) => (
        <Row key={p.id} p={p} i={i} onOpen={onOpen} />
      ))}
      <div className="border-t border-[#1f1f1f]" />
    </div>
  </section>
);
